import Dialog from '@mui/material/Dialog';
import React from 'react';
import type { AdminApiError } from '../../types';
import type {
  AdminDemoLeadConversionInput,
  AdminDemoLeadConversionResult,
  AdminDemoLeadDetail,
} from '../../legalTypes';
import { AdminButton } from '../components';

interface ConversionDraft {
  legalName: string;
  pipeexLegalEntity: string;
  contractingMode: string;
  registrationNumber: string;
  taxId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  jurisdiction: string;
  locale: string;
  signatoryName: string;
  signatoryTitle: string;
  signatoryEmail: string;
  processingRole: string;
  transferMechanism: string;
  enabledProductProcessing: string[];
  createDemoLicence: boolean;
  sendInvitation: boolean;
}

type DraftTextKey = {
  [Key in keyof ConversionDraft]: ConversionDraft[Key] extends string ? Key : never
}[keyof ConversionDraft];

const draftForLead = (lead: AdminDemoLeadDetail): ConversionDraft => ({
  legalName: lead.companyName,
  pipeexLegalEntity: '',
  contractingMode: '',
  registrationNumber: '',
  taxId: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: lead.country,
  jurisdiction: '',
  locale: '',
  signatoryName: `${lead.firstName} ${lead.lastName}`.trim(),
  signatoryTitle: lead.jobTitle,
  signatoryEmail: lead.email,
  processingRole: '',
  transferMechanism: '',
  enabledProductProcessing: [],
  createDemoLicence: false,
  sendInvitation: false,
});

const RequiredFields: ReadonlyArray<{ key: DraftTextKey; label: string }> = [
  { key: 'legalName', label: 'Verified legal name' },
  { key: 'pipeexLegalEntity', label: 'PipeEX legal entity' },
  { key: 'contractingMode', label: 'Contracting mode' },
  { key: 'registrationNumber', label: 'Registration number' },
  { key: 'taxId', label: 'Tax ID' },
  { key: 'addressLine1', label: 'Address line 1' },
  { key: 'city', label: 'City' },
  { key: 'postalCode', label: 'Postal code' },
  { key: 'country', label: 'Country' },
  { key: 'jurisdiction', label: 'Jurisdiction' },
  { key: 'locale', label: 'Language and locale' },
  { key: 'signatoryName', label: 'Authorized contact name' },
  { key: 'signatoryTitle', label: 'Authorized contact title' },
  { key: 'signatoryEmail', label: 'Authorized contact email' },
  { key: 'processingRole', label: 'Processing role' },
  { key: 'transferMechanism', label: 'Transfer choice' },
];

const validateDraft = (draft: ConversionDraft): string[] => {
  const errors = RequiredFields
    .filter((field) => draft[field.key].trim() === '')
    .map((field) => `${field.label} is required.`);
  if (!/^\S+@\S+\.\S+$/.test(draft.signatoryEmail)) {
    errors.push('Authorized contact email must be valid.');
  }
  if (draft.enabledProductProcessing.length === 0) {
    errors.push('Select at least one product processing purpose.');
  }
  return errors;
};

interface FieldGroupProps {
  draft: ConversionDraft;
  onText: (key: DraftTextKey, value: string) => void;
}

const IdentityFields: React.FC<FieldGroupProps> = ({ draft, onText }) => (
  <fieldset className="admin-legal-fieldset">
    <legend>Verified legal identity</legend>
    <div className="admin-legal-form-grid">
      <label>Verified legal name<input autoFocus value={draft.legalName} onChange={(event) => onText('legalName', event.target.value)} /></label>
      <label>PipeEX legal entity<input value={draft.pipeexLegalEntity} onChange={(event) => onText('pipeexLegalEntity', event.target.value)} /></label>
    </div>
    <div className="admin-legal-form-grid">
      <label>Registration number<input value={draft.registrationNumber} onChange={(event) => onText('registrationNumber', event.target.value)} /></label>
      <label>Tax ID<input value={draft.taxId} onChange={(event) => onText('taxId', event.target.value)} /></label>
    </div>
  </fieldset>
);

const AddressFields: React.FC<FieldGroupProps> = ({ draft, onText }) => (
  <fieldset className="admin-legal-fieldset">
    <legend>Registered address and jurisdiction</legend>
    <label>Address line 1<input value={draft.addressLine1} onChange={(event) => onText('addressLine1', event.target.value)} /></label>
    <label>Address line 2 <span className="admin-form-optional">Optional</span><input value={draft.addressLine2} onChange={(event) => onText('addressLine2', event.target.value)} /></label>
    <div className="admin-legal-form-grid">
      <label>City<input value={draft.city} onChange={(event) => onText('city', event.target.value)} /></label>
      <label>Postal code<input value={draft.postalCode} onChange={(event) => onText('postalCode', event.target.value)} /></label>
      <label>Country<input value={draft.country} onChange={(event) => onText('country', event.target.value)} /></label>
      <label>Jurisdiction<input value={draft.jurisdiction} onChange={(event) => onText('jurisdiction', event.target.value)} /></label>
      <label>Language / locale<input value={draft.locale} onChange={(event) => onText('locale', event.target.value)} /></label>
    </div>
  </fieldset>
);

const ContactFields: React.FC<FieldGroupProps> = ({ draft, onText }) => (
  <fieldset className="admin-legal-fieldset">
    <legend>Authorized contact</legend>
    <div className="admin-legal-form-grid">
      <label>Authorized contact name<input value={draft.signatoryName} onChange={(event) => onText('signatoryName', event.target.value)} /></label>
      <label>Authorized contact title<input value={draft.signatoryTitle} onChange={(event) => onText('signatoryTitle', event.target.value)} /></label>
    </div>
    <label>Authorized contact email<input type="email" value={draft.signatoryEmail} onChange={(event) => onText('signatoryEmail', event.target.value)} /></label>
  </fieldset>
);

interface ProcessingFieldsProps extends FieldGroupProps {
  onToggleProduct: (enabled: boolean) => void;
  onToggleProvision: (key: 'createDemoLicence' | 'sendInvitation', enabled: boolean) => void;
}

const ProcessingFields: React.FC<ProcessingFieldsProps> = ({
  draft,
  onText,
  onToggleProduct,
  onToggleProvision,
}) => (
  <fieldset className="admin-legal-fieldset">
    <legend>Processing and provisioning</legend>
    <div className="admin-legal-form-grid">
      <label>Contracting mode
        <select value={draft.contractingMode} onChange={(event) => onText('contractingMode', event.target.value)}>
          <option value="">Select contracting mode</option>
          <option value="B2B">Business customer (B2B)</option>
          <option value="B2C">Consumer customer (B2C)</option>
        </select>
      </label>
      <label>Processing role
        <select value={draft.processingRole} onChange={(event) => onText('processingRole', event.target.value)}>
          <option value="">Select role</option>
          <option value="Controller">Controller</option>
          <option value="Processor">Processor</option>
          <option value="JointController">Joint controller</option>
        </select>
      </label>
      <label>Transfer choice
        <select value={draft.transferMechanism} onChange={(event) => onText('transferMechanism', event.target.value)}>
          <option value="">Select transfer choice</option>
          <option value="None">No international transfer</option>
          <option value="SCC">Standard contractual clauses</option>
          <option value="Adequacy">Adequacy decision</option>
        </select>
      </label>
    </div>
    <label className="admin-legal-check"><input type="checkbox" checked={draft.enabledProductProcessing.includes('PCF export')} onChange={(event) => onToggleProduct(event.target.checked)} />PCF export processing</label>
    <label className="admin-legal-check"><input type="checkbox" checked={draft.createDemoLicence} onChange={(event) => onToggleProvision('createDemoLicence', event.target.checked)} />Create 30-export demo license</label>
    <label className="admin-legal-check"><input type="checkbox" checked={draft.sendInvitation} onChange={(event) => onToggleProvision('sendInvitation', event.target.checked)} />Send customer invitation</label>
  </fieldset>
);

const ValidationSummary: React.FC<{ errors: string[] }> = ({ errors }) => errors.length > 0 ? (
  <div className="admin-legal-callout admin-legal-callout--danger" role="alert" aria-label="Conversion validation summary" tabIndex={-1}>
    <strong>Complete the required conversion fields.</strong>
    <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
  </div>
) : null;

const ConversionReview: React.FC<{ draft: ConversionDraft }> = ({ draft }) => (
  <div className="admin-legal-review">
    <div className="admin-legal-callout admin-legal-callout--warning">
      This irreversible conversion creates one customer and one document package from the reviewed lead.
    </div>
    <dl className="admin-legal-detail-grid">
      <div><dt>Verified customer</dt><dd>{draft.legalName}</dd></div>
      <div><dt>PipeEX entity</dt><dd>{draft.pipeexLegalEntity}</dd></div>
      <div><dt>Contracting mode</dt><dd>{draft.contractingMode}</dd></div>
      <div><dt>Jurisdiction</dt><dd>{draft.jurisdiction}</dd></div>
      <div><dt>PipeEX legal entity</dt><dd>{draft.pipeexLegalEntity}</dd></div>
      <div><dt>Contracting mode</dt><dd>{draft.contractingMode}</dd></div>
      <div><dt>Processing role</dt><dd>{draft.processingRole}</dd></div>
      <div><dt>Transfer choice</dt><dd>{draft.transferMechanism}</dd></div>
      <div><dt>Product processing</dt><dd>{draft.enabledProductProcessing.join(', ')}</dd></div>
    </dl>
    <ul className="admin-legal-effect-list">
      <li>{draft.createDemoLicence ? 'Create a 30-export demo license.' : 'Do not create a demo license.'}</li>
      <li>{draft.sendInvitation ? 'Queue a customer invitation.' : 'Do not queue a customer invitation.'}</li>
    </ul>
  </div>
);

interface DemoLeadConversionWizardProps {
  open: boolean;
  lead: AdminDemoLeadDetail;
  pending: boolean;
  error: AdminApiError | null;
  conflict: { error: AdminApiError; existing: AdminDemoLeadConversionResult } | null;
  onConvert: (input: AdminDemoLeadConversionInput) => Promise<AdminDemoLeadConversionResult>;
  onClose: () => void;
}

const DemoLeadConversionWizard: React.FC<DemoLeadConversionWizardProps> = ({
  open,
  lead,
  pending,
  error,
  conflict,
  onConvert,
  onClose,
}) => {
  const [draft, setDraft] = React.useState(() => draftForLead(lead));
  const [errors, setErrors] = React.useState<string[]>([]);
  const [reviewing, setReviewing] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setDraft(draftForLead(lead));
      setErrors([]);
      setReviewing(false);
    }
  }, [lead, open]);

  const setText = (key: DraftTextKey, value: string) => setDraft((current) => ({ ...current, [key]: value }));
  const review = () => {
    const validation = validateDraft(draft);
    setErrors(validation);
    setReviewing(validation.length === 0);
  };
  const submit = async () => {
    const input: AdminDemoLeadConversionInput = {
      ...draft,
      addressLine2: draft.addressLine2 || null,
      expectedVersion: lead.version,
    };
    try {
      await onConvert(input);
      onClose();
    } catch {
      return;
    }
  };

  return (
    <Dialog open={open} aria-labelledby="demo-lead-conversion-title" maxWidth="md" fullWidth className="pipeex-admin-portal admin-legal-dialog" onClose={() => !pending && onClose()}>
      <form className="admin-legal-dialog__body" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <header><span className="admin-legal-eyebrow">Reviewed lead · version <span data-visual-mask>{lead.version}</span></span><h2 id="demo-lead-conversion-title">Convert demo lead</h2><p>{lead.companyName}</p></header>
        {!reviewing ? <>
          <ValidationSummary errors={errors} />
          <IdentityFields draft={draft} onText={setText} />
          <AddressFields draft={draft} onText={setText} />
          <ContactFields draft={draft} onText={setText} />
          <ProcessingFields draft={draft} onText={setText} onToggleProduct={(enabled) => setDraft((current) => ({ ...current, enabledProductProcessing: enabled ? ['PCF export'] : [] }))} onToggleProvision={(key, enabled) => setDraft((current) => ({ ...current, [key]: enabled }))} />
        </> : <>
          <ConversionReview draft={draft} />
          {conflict && <div role="alert" aria-label="Conversion conflict" className="admin-legal-callout admin-legal-callout--danger"><strong>{conflict.error.message}</strong><p>Existing package: <span data-visual-mask>{conflict.existing.packageId}</span></p></div>}
          {!conflict && error && <div role="alert" className="admin-legal-callout admin-legal-callout--danger">{error.message}</div>}
        </>}
        <footer className="admin-legal-dialog__actions">
          <AdminButton variant="ghost" disabled={pending} onClick={onClose}>Cancel conversion</AdminButton>
          {reviewing && <AdminButton disabled={pending} onClick={() => setReviewing(false)}>Back to fields</AdminButton>}
          {!reviewing ? <AdminButton key="review" variant="primary" onClick={review}>Review conversion</AdminButton> : !conflict && <AdminButton key="submit" type="submit" variant="primary" disabled={pending}>{error ? 'Retry conversion' : 'Create customer and package'}</AdminButton>}
        </footer>
      </form>
    </Dialog>
  );
};

export default DemoLeadConversionWizard;
